/**
 * Invoice value by recorded payment method (Asana 1219713655457321).
 *
 * The payment-method list, its summary and its CSV follow the Reports page:
 * its dates (by invoice date), work category and WO vendor. The figures are
 * BILL TOTALS by the method recorded on the bill. TruckBuddy records no
 * payments, so nothing here is "paid", and a credit applied to a bill changes
 * neither its method nor its value.
 *
 * Its own module rather than `purchase-payment.ts`, because the query extends
 * `reportsQuery` and `requests.ts` already imports `purchase-payment.ts`: both
 * use the other at module load, so the cycle would fail with a ReferenceError.
 */
import { z } from "zod";
import { reportsQuery } from "./requests.js";
import type { ReportJobFilter } from "./category-period.js";
import type { PurchasePaymentRow } from "./purchase-payment.js";

/** The five method groups; `unknown` is a bill with no method recorded. */
export const purchasePaymentMethodKeys = ["ach", "credit_card", "efs", "other", "unknown"] as const;
export type PurchasePaymentMethodKey = (typeof purchasePaymentMethodKeys)[number];

/** A method group's name on screen and in the CSV. */
export function purchasePaymentMethodName(method: PurchasePaymentMethodKey): string {
  switch (method) {
    case "ach": return "ACH";
    case "credit_card": return "Credit card";
    case "efs": return "EFS";
    case "other": return "Other";
    case "unknown": return "Not recorded";
  }
}

/**
 * Whether a bill counts in the summary. Only void leaves it out; disputed and
 * part-settled bills count. The list keeps void bills and says so.
 */
export function isCountedPurchaseBill(status: string): boolean {
  return status !== "void";
}

/**
 * The list, summary and CSV query: the Reports page's scope (`reportsQuery`,
 * with its range checks) plus the list's own method and card filters.
 *
 * `workOrderId` and `purchaseOrderId` are the work-order and purchase-order
 * dialogs' reads. With either set no date default applies (only a start or end
 * sent explicitly), so a dialog lists every bill on its record as before.
 */
export const purchasePaymentQuery = reportsQuery.safeExtend({
  method: z.enum(purchasePaymentMethodKeys).optional(),
  cardLast4: z.string().regex(/^[0-9]{4}$/).optional(),
  workOrderId: z.uuid().optional(),
  purchaseOrderId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
/** What a client sends (every field optional, `timeframe` included). */
export type PurchasePaymentQuery = z.input<typeof purchasePaymentQuery>;
/** The page scope the Reports screen hands the panel. */
export type PurchasePaymentScope = Pick<PurchasePaymentQuery, "timeframe" | "start" | "end" | "jobCategoryId" | "vendorId">;

/** A count of bills and their summed bill totals, in one currency. */
export interface PurchasePaymentAmount {
  currency: string;
  invoices: number;
  amount: string;
}

/** What `GET /reports/purchase-payments/summary` answers. */
export interface PurchasePaymentSummary {
  /** The invoice dates covered; `start` null is Lifetime. */
  start: string | null;
  end: string;
  filters: ReportJobFilter;
  /** Counted bills (every status but void), one row per recorded method and currency. A method with none is absent. */
  rows: Array<PurchasePaymentAmount & { method: PurchasePaymentMethodKey }>;
  /** The counted bills, by currency. Equals the sum of `rows`. */
  total: PurchasePaymentAmount[];
  /** Void bills in the same scope, reported and not counted. */
  voided: PurchasePaymentAmount[];
  /**
   * Under a work-category or WO vendor filter only (null otherwise): counted bills
   * in the dates that are on no work order, such as a stock restock. The filter
   * selects whole work orders, so these drop out, and this line says how many.
   */
  noWorkOrder: PurchasePaymentAmount[] | null;
}

/** What `GET /reports/purchase-payments` answers: a page of bills, and every matching counted bill's totals. */
export interface PurchasePaymentListResponse {
  data: PurchasePaymentRow[];
  /** Counted (not void) bills matching every filter, across all pages, by currency. */
  totals: PurchasePaymentAmount[];
}
